// Copyright (c) 2025 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { catchError, filter, map, mergeMap, withLatestFrom } from "rxjs/operators"
import { create } from "@bufbuild/protobuf"
import * as Slice from "../slices/timeline"
import * as Actions from "../actions"
import { showSnackBar } from "../../snackbar/actions"
import type { RootState } from "../../../app/store"
import { getTimesheetFieldLabel } from "../timesheet/timesheet-fields"
import { Code, from, Init, isConnectError, timelineService } from "../../../app/rpc"
import {
  SelectTimesheetFieldOptionRequestSchema,
  TimeGroup,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

type SelectTimesheetFieldOptionRpc = (request: Init<typeof SelectTimesheetFieldOptionRequestSchema>) => Observable<TimeGroup>

export const selectTimesheetFieldOption = (selectTimesheetFieldOptionRpc: SelectTimesheetFieldOptionRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.selectTimesheetFieldOption.match),
      withLatestFrom(state$),
      mergeMap(([action, state]) => selectTimesheetFieldOptionRpc(action.payload)
        .pipe(
          map(timeGroup => timeGroup),
          mergeMap((timeGroup) => concat(
            of(Slice.upsertTimeGroup(timeGroup)),
            of(Actions.generateTimesheetFieldConfigs([timeGroup])),
            of(Actions.listRecentTimesheetFieldOptions({ siteId: action.payload.siteId })),
          )),
          catchError(e => {
            const timesheetField = state.timeline.timesheetFieldsCache[action.payload.timesheetFieldId ?? ""]
            const fieldName = timesheetField
              ? getTimesheetFieldLabel(timesheetField)
              : t({ id: "msg_timesheet_field", message: "Timesheet field" }).toLowerCase()
            return handleError(e, fieldName)
          }),
        )
      ),
    )

const handleError = (e: unknown, fieldName: string): Observable<Action> => {
  const actions: Action[] = []
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.NotFound) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_select_timesheet_field_option_not_found", 
            message: "Failed to select {fieldName} value, {fieldName} or value not found.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === Code.PermissionDenied) {
      actions.push(showSnackBar({
        message: t({ 
          id: "msg_select_timesheet_field_option_permission_denied", 
          message: "Failed to select {fieldName} value. You do not have permission.",
          values: { fieldName },
        }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
    if (code === Code.FailedPrecondition) {
      actions.push(
        showSnackBar({
          message: t({ 
            id: "msg_select_timesheet_field_option_failed_precondition", 
            message: "Failed to select {fieldName} value. The timesheet entry has already been posted or is out of sync.",
            values: { fieldName },
          }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({
      id: "msg_select_timesheet_field_option_failed",
      message: "Failed to select {fieldName} value. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com.",
      values: { fieldName },
    }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const selectTimesheetFieldOptionRpc = (request: Init<typeof SelectTimesheetFieldOptionRequestSchema>): Observable<TimeGroup> =>
  from(() => timelineService.selectTimesheetFieldOption(create(SelectTimesheetFieldOptionRequestSchema, request)))

export default selectTimesheetFieldOption(selectTimesheetFieldOptionRpc)
